import type { DecodedBrowserAppointment } from './types';
import type { Session } from '@/types';

export type AppointmentTimelineItem =
  | {
      appointment: DecodedBrowserAppointment;
      id: string;
      kind: 'appointment';
      sourceAppointmentId: string;
      sourceAppointmentSeriesId: string | null;
      sourceAppointmentSeriesMutationVersion: number;
      sourceAppointmentVersion: number;
      startAt: string;
    }
  | {
      id: string;
      kind: 'session';
      session: Session;
      startAt: string;
    };

export const buildAppointmentTimelineItems = (
  appointments: DecodedBrowserAppointment[],
  sessions: Session[],
): AppointmentTimelineItem[] => {
  const sessionAppointmentVersions = new Map<string, number>();
  sessions.forEach((session) => {
    if (
      !session.sourceAppointmentId ||
      session.sourceAppointmentVersion === null ||
      session.sourceAppointmentVersion === undefined
    )
      return;
    const previous = sessionAppointmentVersions.get(
      session.sourceAppointmentId,
    );
    if (previous === undefined || session.sourceAppointmentVersion > previous) {
      sessionAppointmentVersions.set(
        session.sourceAppointmentId,
        session.sourceAppointmentVersion,
      );
    }
  });
  const latestAppointmentById = new Map<string, DecodedBrowserAppointment>();

  appointments.forEach((appointment) => {
    const previous = latestAppointmentById.get(appointment.id);
    if (!previous || appointment.version > previous.version) {
      latestAppointmentById.set(appointment.id, appointment);
    }
  });

  const appointmentById = new Map<string, DecodedBrowserAppointment>();
  const suppressedSessionAppointmentIds = new Set<string>();
  latestAppointmentById.forEach((appointment) => {
    const sessionVersion = sessionAppointmentVersions.get(appointment.id);
    if (sessionVersion === undefined || appointment.version > sessionVersion) {
      suppressedSessionAppointmentIds.add(appointment.id);
      if (appointment.lifecycleStatus !== 'cancelled') {
        appointmentById.set(appointment.id, appointment);
      }
    }
  });

  return [
    ...Array.from(appointmentById.values()).map((appointment) => ({
      appointment,
      id: appointment.id,
      kind: 'appointment' as const,
      sourceAppointmentId: appointment.id,
      sourceAppointmentSeriesId: appointment.seriesId,
      sourceAppointmentSeriesMutationVersion: appointment.seriesMutationVersion,
      sourceAppointmentVersion: appointment.version,
      startAt: appointment.currentSchedule.startAt,
    })),
    ...sessions
      .filter((session) => {
        if (!session.sourceAppointmentId) return true;
        return !suppressedSessionAppointmentIds.has(
          session.sourceAppointmentId,
        );
      })
      .map((session) => ({
        id: session.id,
        kind: 'session' as const,
        session,
        startAt: session.startedAt,
      })),
  ].sort((left, right) => {
    const timeDelta = Date.parse(left.startAt) - Date.parse(right.startAt);
    if (timeDelta !== 0) return timeDelta;
    return `${left.kind}:${left.id}`.localeCompare(`${right.kind}:${right.id}`);
  });
};

interface AppointmentTimelineProps {
  appointments: DecodedBrowserAppointment[];
  sessions: Session[];
}

const AppointmentTimeline = ({
  appointments,
  sessions,
}: AppointmentTimelineProps) => (
  <div>
    {buildAppointmentTimelineItems(appointments, sessions).map((item) => (
      <div
        key={`${item.kind}:${item.id}`}
        data-testid="appointment-timeline-item"
      >
        {item.kind === 'appointment'
          ? item.appointment.expectedIdentities.coach.displayName
          : item.session.title}
      </div>
    ))}
  </div>
);

export default AppointmentTimeline;
